import type { Commodity, StrictOmit } from '@enmon/commons';
import {
  type SerializedCustomer,
  type SerializedEstablishment,
  type SerializedSupplyPoint,
  type SerializedMeter,
  type CountableApiResponse,
  type ViewSupplyPoints,
  ImportSupplyPointsFormField,
  type SupplyPointsStats,
  type SerializedSupplyPointByView,
  type UpdateSupplyPointDto,
  type CreateSupplyPointDto,
  type ImportSupplyPointCsvRow,
  type SupplyPointCategoryType,
  type SupplyPointStatus,
  type SerializedTenant,
} from '@enmon/commons/lib/enmon';
import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import type { Pagination } from '@/hooks/usePagination';
import { captureException } from '@sentry/react';
import type { SerializedThirdParty } from '@enmon/commons/lib/tools';
import type { SerializedSupplyPointWithAnalysis } from '@enmon/commons/lib/enmon/supply-points/supplyPointWithAnalysis';
import type { PopulatedCustomer } from './customers';
import type { ImportArgs, ImportResult, WithAttachments } from './imports';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export type PopulatedSupplyPoint = StrictOmit<
  SerializedSupplyPoint,
  'distributor' | 'supplier' | '__v' | 'customer' | 'establishment' | 'owner'
> & {
  distributor?: SerializedThirdParty;
  supplier?: SerializedThirdParty;
  customer: PopulatedCustomer;
  establishment: Pick<SerializedEstablishment, '_id' | 'name' | 'type' | 'customer'>;
  owner?: Pick<SerializedTenant, '_id' | 'name'> | null;
};

export interface SupplyPointsFilter extends Pagination {
  commodities?: Commodity | Commodity[];
  supplyPoints?: SerializedSupplyPoint['_id'] | SerializedSupplyPoint['_id'][];
  meters?: SerializedMeter['_id'] | SerializedMeter['_id'][];
  establishments?: SerializedEstablishment['_id'] | SerializedEstablishment['_id'][];
  tags?: SerializedEstablishment['tags'] | SerializedEstablishment['tags'][];
  customer?: SerializedCustomer['_id'] | SerializedCustomer['_id'][];
  code?: SerializedSupplyPoint['code'];
  search?: string;
  view?: ViewSupplyPoints;
  supplyPointCategories?: SupplyPointCategoryType[];
  supplyPointStatuses?: SupplyPointStatus[];
}

// Argument types
export type GetSupplyPointArg = SerializedSupplyPoint['_id'];
export type GetSupplyPointsArg = SupplyPointsFilter;
export type GetSupplyPointsMetadataArg = SupplyPointsFilter;
export type GetSupplyPointsStats = SupplyPointsFilter;
export type GetSupplyPointsWithAnalysisArg = SupplyPointsFilter;
export type CreateSupplyPointArg = CreateSupplyPointDto;
export type ImportSupplyPointsArg = ImportArgs<WithAttachments>;
export type UpdateSupplyPointArg = {
  supplyPointId: SerializedSupplyPoint['_id'];
  body: UpdateSupplyPointDto;
};
export type MoveSupplyPointArg = {
  supplyPointId: SerializedSupplyPoint['_id'];
  establishmentId: SerializedEstablishment['_id'];
};
export type MoveSupplyPointsBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
  establishmentId: SerializedEstablishment['_id'];
};
export type ChangeSupplierBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
  supplierId: string;
};
export type DeleteSupplyPointArg = SerializedSupplyPoint['_id'];
export type DeleteSupplyPointsBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
};
export type ToggleSupplyPointStatusArg = SerializedSupplyPoint['_id'];
export type GetSupplyPointHasHistoryArg = SerializedSupplyPoint['_id'];
export type SuspendSupplyPointsBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
};

// Result types
export type GetSupplyPointResult = PopulatedSupplyPoint | null;
export type GetSupplyPointsResult = CountableApiResponse<PopulatedSupplyPoint[]> | null;
export type GetSupplyPointsMetadataResult<TView extends ViewSupplyPoints> = CountableApiResponse<
  SerializedSupplyPointByView[TView][]
> | null;
export type GetSupplyPointsStatsResult = SupplyPointsStats | null;
export type GetSupplyPointsWithAnalysisResult = CountableApiResponse<SerializedSupplyPointWithAnalysis[]> | null;
export type CreateSupplyPointResult = PopulatedSupplyPoint | null;
export type ImportSupplyPointsResult = ImportResult<ImportSupplyPointCsvRow> | null;
export type UpdateSupplyPointResult = PopulatedSupplyPoint | null;
export type MoveSupplyPointResult = undefined | null;
export type MoveSupplyPointsBulkResult = undefined | null;
export type ChangeSupplierBulkResult = undefined | null;
export type DeleteSupplyPointResult = { archived: true } | null;
export type DeleteSupplyPointsBulkResult = { deleted: string[]; archived: string[] } | null;
export type ToggleSupplyPointStatusResult = PopulatedSupplyPoint | null;
export type GetSupplyPointHasHistoryResult = { hasHistory: boolean } | null;
export type SuspendSupplyPointsBulkResult = undefined | null;

export class SupplyPointsApiClient extends BaseApiClient {
  constructor() {
    super('api/supply-points');
  }

  public getOne(supplyPointId: GetSupplyPointArg, signal: RequestOptions['signal']): Promise<GetSupplyPointResult> {
    return this.http.get(`${supplyPointId}`, { signal });
  }

  public getHasHistory(
    supplyPointId: GetSupplyPointHasHistoryArg,
    signal: RequestOptions['signal'],
  ): Promise<GetSupplyPointHasHistoryResult> {
    return this.http.get(`${supplyPointId}/has-history`, { signal });
  }

  public get(filter: GetSupplyPointsArg, signal: RequestOptions['signal']): Promise<GetSupplyPointsResult> {
    return this.http.get('', {
      query: getQuery(filter),
      signal,
    });
  }

  public getWithAnalysis(
    filter: SupplyPointsFilter,
    signal: RequestOptions['signal'],
  ): Promise<GetSupplyPointsWithAnalysisResult> {
    return this.http.get('analysis', {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public getMetadata<TView extends ViewSupplyPoints = ViewSupplyPoints.Default>(
    filter: SupplyPointsFilter,
    view: TView,
    signal: RequestOptions['signal'],
  ): Promise<GetSupplyPointsMetadataResult<TView>> {
    return this.http.get('metadata', {
      query: getQuery({ ...filter, view }),
      signal,
    });
  }

  public getStats(filter: SupplyPointsFilter, signal: AbortSignal | undefined): Promise<GetSupplyPointsStatsResult> {
    return this.http.get('stats', { query: getQuery(filter), signal });
  }

  public create(body: CreateSupplyPointArg): Promise<CreateSupplyPointResult> {
    return this.http.post('', body);
  }

  public async importSupplyPoints({
    importFile,
    attachments,
  }: ImportSupplyPointsArg): Promise<ImportSupplyPointsResult> {
    const formData = new FormData();
    formData.append(ImportSupplyPointsFormField.ImportFile, importFile);

    attachments.forEach(attachment => {
      formData.append(ImportSupplyPointsFormField.Attachments, attachment);
    });
    try {
      return await uploadFile(`${this.http.urlPrefix}/import`, formData, {
        signal: undefined,
      });
    } catch (e) {
      if (e instanceof RequestError && e.response.status === 412) {
        return await e.response.json<ImportSupplyPointsResult>();
      }
      console.error(e);
      captureException(e);
      throw e;
    }
  }

  public update({ supplyPointId, body }: UpdateSupplyPointArg): Promise<UpdateSupplyPointResult> {
    return this.http.patch(`${supplyPointId}`, body);
  }

  public move({ supplyPointId, establishmentId }: MoveSupplyPointArg): Promise<MoveSupplyPointResult> {
    return this.http.put(`${supplyPointId}/move`, { establishmentId });
  }

  public moveBulk(body: MoveSupplyPointsBulkArg): Promise<MoveSupplyPointsBulkResult> {
    return this.http.put('bulk-move', body);
  }

  public changeSupplierBulk(body: ChangeSupplierBulkArg): Promise<ChangeSupplierBulkResult> {
    return this.http.put('bulk-change-supplier', body);
  }

  public delete(supplyPointId: DeleteSupplyPointArg): Promise<DeleteSupplyPointResult> {
    return this.http.delete(`${supplyPointId}`);
  }

  public deleteBulk(body: DeleteSupplyPointsBulkArg): Promise<DeleteSupplyPointsBulkResult> {
    return this.http.delete('', { signal: undefined, body: JSON.stringify(body) });
  }

  public toggleStatus(supplyPointId: ToggleSupplyPointStatusArg): Promise<ToggleSupplyPointStatusResult> {
    return this.http.put(`${supplyPointId}/toggle-status`, {});
  }

  public suspendBulk(body: SuspendSupplyPointsBulkArg): Promise<SuspendSupplyPointsBulkResult> {
    return this.http.put('bulk-suspend', body);
  }
}
